import { useBookingFlow } from "./session/BookingFlowProvider";
import { buildSteps, stepAfter, stepBefore } from "./flow/FlowMachine";
import { PartyStep, RoomsStep, StayStep } from "./flow/steps/StaySteps";
import { CategoryStep, ExtrasStep, PreferencesStep, RateStep } from "./flow/steps/ProductSteps";
import { ContactStep, PaymentStep, ReviewStep, StatusStep } from "./flow/steps/CloseSteps";
import { BookingSummary } from "./components/BookingSummary";
import { Notice } from "./components/ui";
import type { FlowStepId } from "./bridge/contract";

function StepBody({ step }: { step: FlowStepId }) {
  switch (step) {
    case "stay":
      return <StayStep />;
    case "rooms":
      return <RoomsStep />;
    case "party":
      return <PartyStep />;
    case "category":
      return <CategoryStep />;
    case "rate":
      return <RateStep />;
    case "preferences":
      return <PreferencesStep />;
    case "extras":
      return <ExtrasStep />;
    case "contact":
      return <ContactStep />;
    case "review":
      return <ReviewStep />;
    case "payment":
      return <PaymentStep />;
    case "status":
      return <StatusStep />;
  }
}

export default function App({ mode }: { mode: "fullpage" | "embed" }) {
  const { bootstrap, projection, step, visited, goTo, pending, conflict, dismissConflict, error, dismissError } =
    useBookingFlow();

  const steps = buildSteps(bootstrap, projection, step, visited);
  const previous = stepBefore(bootstrap, projection, step);
  const next = stepAfter(bootstrap, projection, step);
  const hotel = bootstrap.hotel;
  const position = steps.findIndex((entry) => entry.id === step) + 1;

  return (
    <div className={`booking booking--${mode}`}>
      <header
        className={`booking-head${hotel.branding.heroImageUrl ? " booking-head--hero" : ""}`}
        style={
          hotel.branding.heroImageUrl
            ? { backgroundImage: `url("${hotel.branding.heroImageUrl}")` }
            : undefined
        }
      >
        <div className="booking-head-inner">
          {hotel.branding.logoUrl ? <img src={hotel.branding.logoUrl} alt="" className="hotel-logo" /> : null}
          <div>
            <h1>{hotel.name}</h1>
            {hotel.city ? <p className="muted">{hotel.city}</p> : null}
          </div>
        </div>
      </header>

      <nav className="stepper-nav" aria-label="Buchungsschritte">
        <p className="stepper-progress" aria-hidden="true">
          Schritt {position} von {steps.length}
        </p>
        <ol>
          {steps.map((entry) => (
            <li key={entry.id} className={`step step--${entry.state}`}>
              <button
                type="button"
                onClick={() => goTo(entry.id)}
                aria-current={entry.state === "current" ? "step" : undefined}
              >
                {entry.label}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {conflict ? (
        <Notice tone="warning" title="Es hat sich etwas geändert.">
          <p>
            {conflict.code === "QUOTE_EXPIRED"
              ? "Das Angebot ist abgelaufen. Bitte lassen Sie den Preis neu berechnen — wir ändern nichts stillschweigend."
              : "Die Buchung wurde zwischenzeitlich aktualisiert. Oben sehen Sie den aktuellen Stand."}
          </p>
          <button type="button" className="button button--ghost" onClick={dismissConflict}>
            Verstanden
          </button>
        </Notice>
      ) : null}

      {error ? (
        <Notice tone="error" title="Das hat nicht geklappt.">
          <p>{error}</p>
          <button type="button" className="button button--ghost" onClick={dismissError}>
            Schließen
          </button>
        </Notice>
      ) : null}

      <div className="booking-layout">
        <main className="booking-body" aria-busy={pending}>
          <StepBody step={step} />
        </main>
        {/* On review and payment the quote already is the summary — repeating the total right
            under it reads as two different numbers at a glance. */}
        {step === "review" || step === "payment" || step === "status" ? null : (
          <BookingSummary bootstrap={bootstrap} projection={projection} />
        )}
      </div>

      <footer className="booking-foot">
        <button
          type="button"
          className="button button--ghost"
          disabled={!previous || pending}
          onClick={() => previous && goTo(previous)}
        >
          Zurück
        </button>
        <button
          type="button"
          className="button"
          disabled={!next || pending}
          onClick={() => next && goTo(next)}
        >
          Weiter
        </button>
      </footer>
    </div>
  );
}
